import { Link } from "react-router-dom";
import { CalendarPlus, Eye, Pencil } from "lucide-react";
import { SeloEstado, SeloFaixa, SeloTexto } from "@/modules/visitantes/components/Selos";
import type { VisitanteLista } from "@/modules/visitantes/services/visitantesService";
import {
  calcularIdade,
  estadoDe,
  foraDoAcompanhamento,
  formatarData,
  voltouAposContato,
} from "@/modules/visitantes/utils/regras";

const BOTAO =
  "inline-flex min-h-9 items-center gap-1.5 rounded-lg border border-border bg-transparent px-3 text-[13px] font-semibold text-text transition-colors hover:bg-surface-2";

interface Props {
  visitante: VisitanteLista;
  /** Só o ministério responsável edita e registra nova visita. */
  podeEscrever: boolean;
  novo?: boolean;
  onNovaVisita: (v: VisitanteLista) => void;
}

/** Uma linha da lista. O telefone vem mascarado; ligar/WhatsApp só existem dentro da ficha. */
export function LinhaVisitante({ visitante: v, podeEscrever, novo, onNovaVisita }: Props) {
  const idade = calcularIdade(v.nascimento);
  const estado = estadoDe(v);
  // Criança sem contato fica fora do acompanhamento (o contato é com o responsável).
  const mostraEstado = !foraDoAcompanhamento(v);
  const naoProcurar = v.preferencia_contato === "nao";
  const destino = `/visitantes/${v.id}`;

  return (
    <li className="rounded-xl border border-border bg-surface p-4">
      <div className="flex flex-wrap items-start justify-between gap-2">
        <div className="min-w-0">
          <Link
            to={destino}
            className="block truncate text-base font-bold text-text hover:underline"
          >
            {v.nome}
          </Link>
          <p className="mt-0.5 text-xs text-muted">
            {v.ultima_visita
              ? `Última visita ${formatarData(v.ultima_visita)}`
              : "Sem visita registrada"}{" "}
            · {v.visitas} {v.visitas === 1 ? "visita" : "visitas"}
            {v.telefone_mascarado ? ` · ${v.telefone_mascarado}` : ""}
          </p>
        </div>
        {novo && <SeloTexto tom="ok">Novo</SeloTexto>}
      </div>

      <div className="mt-2 flex flex-wrap items-center gap-1.5">
        <SeloFaixa idade={idade} />
        {mostraEstado && <SeloEstado estado={estado} />}
        {voltouAposContato(v) && <SeloTexto tom="ok">Voltou após o último contato</SeloTexto>}
        {naoProcurar && <SeloTexto tom="aviso">Prefere não ser procurado(a)</SeloTexto>}
        {podeEscrever && v.sem_responsavel && idade !== null && idade < 12 && (
          <SeloTexto tom="perigo">Sem responsável</SeloTexto>
        )}
        {podeEscrever && v.informacoes_novas > 0 && (
          <SeloTexto tom="aviso">{`${v.informacoes_novas} ${v.informacoes_novas === 1 ? "informação nova" : "informações novas"}`}</SeloTexto>
        )}
      </div>

      <div className="mt-3 flex flex-wrap gap-2">
        <Link to={destino} className={BOTAO}>
          <Eye size={14} aria-hidden="true" /> Ver ficha
        </Link>
        {podeEscrever && (
          <>
            <Link to={`${destino}/editar`} className={BOTAO}>
              <Pencil size={14} aria-hidden="true" /> Editar
            </Link>
            <button type="button" onClick={() => onNovaVisita(v)} className={BOTAO}>
              <CalendarPlus size={14} aria-hidden="true" /> Nova visita
            </button>
          </>
        )}
      </div>
    </li>
  );
}
